import { useQuery } from '@tanstack/react-query';
import { useNostr } from '@nostrify/react';
import type { NostrEvent } from '@nostrify/nostrify';
import { TORCH_GENESIS_KIND, TORCH_PASS_KIND } from '@/lib/torchConstants';
import { parseGenesisEvent, parsePassEvent, buildCanonicalChain, genesisRef } from '@/lib/torchEvents';
import type { GenesisEvent, TorchChain, ParsedChain } from '@/lib/torchTypes';

/** Fetch all genesis events — the torch directory (genesis metadata only, no chain state). */
export function useTorchDirectory() {
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['torch-directory'],
    queryFn: async ({ signal }) => {
      const events = await nostr.query([{
        kinds: [TORCH_GENESIS_KIND],
        limit: 50,
      }], { signal });

      const torches: GenesisEvent[] = [];
      for (const event of events) {
        const parsed = parseGenesisEvent(event);
        if (parsed) torches.push(parsed);
      }

      return torches;
    },
  });
}

/**
 * Fetch all torches with their fully-resolved chain state in two batched relay queries.
 * Returns ParsedChain[] so callers know each torch's state, holder set, and expiry
 * without firing a separate per-torch query.
 */
export function useTorchDirectoryFull() {
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['torch-directory-full'],
    queryFn: async ({ signal }) => {
      // 1. Fetch all genesis events
      const genesisEvents = await nostr.query([{
        kinds: [TORCH_GENESIS_KIND],
        limit: 50,
      }], { signal });

      const genesisList: GenesisEvent[] = genesisEvents
        .map(parseGenesisEvent)
        .filter(Boolean) as GenesisEvent[];

      if (genesisList.length === 0) return [];

      // 2. Fetch all pass events for all torches in one query using their refs
      const refs = genesisList.map(g => genesisRef(g));
      const passEvents = await nostr.query([{
        kinds: [TORCH_PASS_KIND],
        '#a': refs,
        limit: 2000,
      }], { signal });

      // 3. Group passes by genesis ref and build each chain
      const passesByRef = new Map<string, ReturnType<typeof parsePassEvent>[]>();
      for (const event of passEvents) {
        const pass = parsePassEvent(event);
        if (!pass) continue;
        const ref = pass.genesisRef;
        if (!passesByRef.has(ref)) passesByRef.set(ref, []);
        passesByRef.get(ref)!.push(pass);
      }

      const chains: ParsedChain[] = genesisList.map(genesis => {
        const ref = genesisRef(genesis);
        const passes = (passesByRef.get(ref) ?? []).filter(Boolean) as NonNullable<ReturnType<typeof parsePassEvent>>[];
        const chain: TorchChain = { genesis, passes };
        return buildCanonicalChain(chain);
      });

      return chains;
    },
    // Refresh every 60 s so state changes propagate without a manual reload
    refetchInterval: 60_000,
  });
}

/** Fetch a torch by its genesis ref string ("30910:pubkey:slug") and build the chain. */
export function useTorchDetail(genesisRefStr: string) {
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['torch-detail', genesisRefStr],
    queryFn: async ({ signal }) => {
      if (!genesisRefStr) return null;

      const parts = genesisRefStr.split(':');
      if (parts.length !== 3) return null;

      const [kindStr, pubkey, slug] = parts;
      const kind = parseInt(kindStr, 10);

      const genesisEvents = await nostr.query([{
        kinds: [kind],
        authors: [pubkey],
        '#d': [slug],
        limit: 1,
      }], { signal });

      const genesis = genesisEvents[0] ? parseGenesisEvent(genesisEvents[0]) : null;
      if (!genesis) return null;

      const passEvents = await nostr.query([{
        kinds: [TORCH_PASS_KIND],
        '#a': [genesisRefStr],
        limit: 500,
      }], { signal });

      const chain: TorchChain = {
        genesis,
        passes: passEvents.map(parsePassEvent).filter(Boolean) as NonNullable<ReturnType<typeof parsePassEvent>>[],
      };

      return buildCanonicalChain(chain);
    },
    enabled: !!genesisRefStr,
  });
}

/** Fetch a torch by slug and build the chain. Used by most pages. */
export function useTorchBySlug(slug: string) {
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['torch-slug', slug],
    queryFn: async ({ signal }) => {
      if (!slug) return null;

      // Find genesis — no author filter here since we don't know it from slug alone
      const genesisEvents = await nostr.query([{
        kinds: [TORCH_GENESIS_KIND],
        '#d': [slug],
        limit: 1,
      }], { signal });

      const genesis = genesisEvents[0] ? parseGenesisEvent(genesisEvents[0]) : null;
      if (!genesis) return null;

      const genesisRefStr = `${TORCH_GENESIS_KIND}:${genesis.pubkey}:${genesis.slug}`;

      const passEvents = await nostr.query([{
        kinds: [TORCH_PASS_KIND],
        '#a': [genesisRefStr],
        limit: 500,
      }], { signal });

      const chain: TorchChain = {
        genesis,
        passes: passEvents.map(parsePassEvent).filter(Boolean) as NonNullable<ReturnType<typeof parsePassEvent>>[],
      };

      return buildCanonicalChain(chain);
    },
    enabled: !!slug,
  });
}

/**
 * Fetch all torches a pubkey has been involved with — as genesis creator,
 * passer, or recipient. Returns fully resolved ParsedChain[] sorted with
 * lit torches first, then extinguished, newest first within each group.
 */
export function useProfileTorches(pubkey: string | undefined) {
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['profile-torches', pubkey ?? ''],
    queryFn: async ({ signal }): Promise<ParsedChain[]> => {
      if (!pubkey) return [];

      // 1. Torches they created (genesis author)
      const genesisEvents = await nostr.query([{
        kinds: [TORCH_GENESIS_KIND],
        authors: [pubkey],
        limit: 100,
      }], { signal });

      // 2. Passes they were involved in (as passer or recipient)
      const passEvents = await nostr.query([
        { kinds: [TORCH_PASS_KIND], authors: [pubkey], limit: 200 },
        { kinds: [TORCH_PASS_KIND], '#p': [pubkey],   limit: 200 },
      ], { signal });

      const parsedPasses = passEvents
        .map(parsePassEvent)
        .filter(Boolean) as NonNullable<ReturnType<typeof parsePassEvent>>[];

      // Collect all unique genesis refs we need to resolve
      const genesisRefSet = new Set<string>();
      for (const p of parsedPasses) genesisRefSet.add(p.genesisRef);

      // Fetch any genesis events not already loaded from their ref
      const existingGenesisByRef = new Map<string, GenesisEvent>();
      for (const ev of genesisEvents) {
        const g = parseGenesisEvent(ev);
        if (g) existingGenesisByRef.set(genesisRef(g), g);
      }

      const missingRefs = Array.from(genesisRefSet).filter(r => !existingGenesisByRef.has(r));
      if (missingRefs.length > 0) {
        // Parse "kind:pubkey:slug" refs to build targeted queries
        const missingQueries = missingRefs.flatMap(ref => {
          const parts = ref.split(':');
          if (parts.length !== 3) return [];
          const [kindStr, pk, slug] = parts;
          return [{ kinds: [parseInt(kindStr, 10)], authors: [pk], '#d': [slug], limit: 1 }];
        });
        if (missingQueries.length > 0) {
          const extraEvents = await nostr.query(missingQueries, { signal });
          for (const ev of extraEvents) {
            const g = parseGenesisEvent(ev);
            if (g) existingGenesisByRef.set(genesisRef(g), g);
          }
        }
      }

      if (existingGenesisByRef.size === 0) return [];

      // 3. Fetch ALL passes for the torches we found (to build correct chains)
      const allRefs = Array.from(existingGenesisByRef.keys());
      const allPassEvents = await nostr.query([{
        kinds: [TORCH_PASS_KIND],
        '#a': allRefs,
        limit: 2000,
      }], { signal });

      const allPasses = allPassEvents
        .map(parsePassEvent)
        .filter(Boolean) as NonNullable<ReturnType<typeof parsePassEvent>>[];

      const passesByRef = new Map<string, typeof allPasses>();
      for (const p of allPasses) {
        if (!passesByRef.has(p.genesisRef)) passesByRef.set(p.genesisRef, []);
        passesByRef.get(p.genesisRef)!.push(p);
      }

      const chains: ParsedChain[] = Array.from(existingGenesisByRef.values()).map(genesis => {
        const ref = genesisRef(genesis);
        const passes = passesByRef.get(ref) ?? [];
        return buildCanonicalChain({ genesis, passes });
      });

      // Lit first, then extinguished; newest genesis within each group
      return chains.sort((a, b) => {
        if (a.state !== b.state) return a.state === 'lit' ? -1 : 1;
        return b.genesis.createdAt - a.genesis.createdAt;
      });
    },
    enabled: !!pubkey,
  });
}

/**
 * Fetch kind 1 notes tagged #torchlite, filtered to only include posts from
 * pubkeys that have actually held at least one torch (genesis creators + pass recipients).
 *
 * holderPubkeys — the full set of known torch participant pubkeys, derived from
 * useTorchDirectoryFull. When empty, the query is skipped.
 */
export function useTorchliteFeed(holderPubkeys: Set<string>) {
  const { nostr } = useNostr();
  const authors = Array.from(holderPubkeys);

  return useQuery({
    queryKey: ['torchlite-feed', authors.slice().sort().join(',')],
    queryFn: async ({ signal }): Promise<NostrEvent[]> => {
      if (authors.length === 0) return [];

      const events = await nostr.query([{
        kinds: [1],
        '#t': ['torchlite'],
        authors,
        limit: 30,
      }], { signal });

      // Sort newest-first
      return [...events].sort((a, b) => b.created_at - a.created_at);
    },
    enabled: holderPubkeys.size > 0,
    refetchInterval: 60_000,
  });
}
